import { useEffect, useRef, useState } from 'react';
import {
  FullscreenControl,
  Map as MapLibreMap,
  NavigationControl,
  setWorkerUrl,
  type GeoJSONSource,
  type StyleSpecification,
} from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { JobMapPoint } from '@cer-autofill/shared';
import { fetchMapStyle, mapTileRequestTransform } from '../api.js';
import { shouldFallBackToSchematic } from '../lib/map-availability.js';
import { mapLibreWorkerUrl } from '../lib/map-worker.js';

// MapLibre resolves its worker with `new URL('./maplibre-gl-worker.mjs',
// import.meta.url)` inside the dependency, which the bundler cannot see.
// Serve the official worker + shared sibling from /maplibre/ (copied at
// build time) so Vite 8 cannot drop either file and CSP `worker-src 'self'`
// can load them. A silent worker miss used to look like a successful map
// that then swapped to the schematic — see shouldFallBackToSchematic.
setWorkerUrl(
  mapLibreWorkerUrl(
    typeof window !== 'undefined' ? window.location.origin : 'http://localhost',
    import.meta.env.BASE_URL ?? '/',
  ),
);

/** Bounds of the Australian mainland plus Tasmania, used as the home view. */
const AUSTRALIA_BOUNDS: [[number, number], [number, number]] = [
  [112, -44],
  [154, -10],
];

const TONE_COLOUR: Record<JobMapPoint['tone'], string> = {
  allocated: '#2563eb',
  scheduled: '#7c3aed',
  complete: '#16a34a',
};

/**
 * Interactive Job Locations map.
 *
 * MapLibre renders Amazon Location vector tiles (proxied through the backend
 * so the browser never sees an Amazon tile URL) and does the clustering
 * itself: `cluster: true` on the GeoJSON source means clusters split into
 * individual job pins as you zoom, with no bookkeeping on our side.
 *
 * If the style fetch throws, or a tile/style request comes back 401 / 403 /
 * 503, `onUnavailable` fires and the caller falls back to the schematic.
 * An idle map with an unparsed vector basemap is *not* unavailable — that
 * silent worker miss used to trip an 8s watchdog and become the default
 * Leading Edge view. The clusters carry the same data either way.
 */
export function JobLocationsMap({
  points,
  onSelectJob,
  onUnavailable,
}: {
  points: JobMapPoint[];
  onSelectJob: (jobId: string) => void;
  onUnavailable: () => void;
}) {
  /**
   * `?mapdebug=1` keeps a failing map on screen instead of swapping in the
   * schematic, and records what MapLibre reports, so a failure that only
   * reproduces on someone else's machine can be inspected there. Opt-in only —
   * normal loads are unaffected.
   */
  const debug =
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('mapdebug');
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const [ready, setReady] = useState(false);

  // Build the map once; points are pushed into the source separately so a
  // filter change doesn't tear down and re-create the whole GL context.
  useEffect(() => {
    let cancelled = false;
    let map: MapLibreMap | undefined;
    let resizeObserver: ResizeObserver | undefined;

    void (async () => {
      try {
        const style = (await fetchMapStyle()) as StyleSpecification;
        const container = containerRef.current;
        if (cancelled || !container) return;

        const gl = new MapLibreMap({
          container,
          style,
          bounds: AUSTRALIA_BOUNDS,
          fitBoundsOptions: { padding: 24 },
          attributionControl: { compact: true },
          transformRequest: (url: string) => mapTileRequestTransform(url),
        });
        map = gl;
        mapRef.current = gl;
        gl.addControl(new NavigationControl({ showCompass: false }), 'bottom-right');
        gl.addControl(new FullscreenControl(), 'bottom-right');

        if (debug) {
          const w = window as unknown as { __map?: unknown; __mapEvents?: unknown[] };
          w.__map = gl;
          w.__mapEvents = [];
          for (const ev of ['error', 'styledata', 'sourcedata', 'idle', 'dataloading']) {
            // One handler over five event types whose payloads differ, so the
            // fields are read defensively rather than declared. Typed `unknown`
            // and narrowed inside: MapLibre 6.3 tightened the `error` overload
            // to a payload whose `error` is required, which a single loose
            // parameter type can no longer satisfy for all five.
            gl.on(ev as 'error', (e: unknown) => {
              const detail = e as
                { error?: Error; sourceId?: string; isSourceLoaded?: boolean } | undefined;
              w.__mapEvents!.push({
                ev,
                src: detail?.sourceId,
                loaded: detail?.isSourceLoaded,
                err: detail?.error?.message,
              });
            });
          }
        }

        gl.on('error', (event) => {
          // A single failed tile shouldn't tear the map down. Only an
          // authenticated or disabled style/tile response is last-resort.
          const status = (event.error as { status?: number } | undefined)?.status;
          if (status !== undefined && shouldFallBackToSchematic({ kind: 'http-status', status })) {
            onUnavailable();
          }
        });

        gl.on('load', () => {
          if (cancelled) return;
          gl.addSource('jobs', {
            type: 'geojson',
            data: { type: 'FeatureCollection', features: [] },
            cluster: true,
            clusterRadius: 48,
            clusterMaxZoom: 12,
          });

          gl.addLayer({
            id: 'job-clusters',
            type: 'circle',
            source: 'jobs',
            filter: ['has', 'point_count'],
            paint: {
              'circle-color': '#2563eb',
              'circle-opacity': 0.92,
              'circle-stroke-width': 3,
              'circle-stroke-color': '#ffffff',
              // Grow with the number of jobs behind the badge.
              'circle-radius': ['step', ['get', 'point_count'], 16, 10, 21, 50, 26, 250, 32],
            },
          });
          gl.addLayer({
            id: 'job-cluster-count',
            type: 'symbol',
            source: 'jobs',
            filter: ['has', 'point_count'],
            layout: {
              'text-field': ['get', 'point_count_abbreviated'],
              'text-font': ['Amazon Ember Bold'],
              'text-size': 13,
            },
            paint: { 'text-color': '#ffffff' },
          });
          gl.addLayer({
            id: 'job-pins',
            type: 'circle',
            source: 'jobs',
            filter: ['!', ['has', 'point_count']],
            paint: {
              'circle-color': [
                'match',
                ['get', 'tone'],
                'scheduled',
                TONE_COLOUR.scheduled,
                'complete',
                TONE_COLOUR.complete,
                TONE_COLOUR.allocated,
              ],
              'circle-radius': 7,
              'circle-stroke-width': 2,
              'circle-stroke-color': '#ffffff',
            },
          });

          // Clicking a cluster zooms far enough to break it apart.
          gl.on('click', 'job-clusters', (event) => {
            const feature = event.features?.[0];
            const clusterId: unknown = feature?.properties?.['cluster_id'];
            if (typeof clusterId !== 'number' || !feature) return;
            const source = gl.getSource('jobs');
            void (source as GeoJSONSource | undefined)
              ?.getClusterExpansionZoom(clusterId)
              .then((zoom) => {
                const [lng, lat] = (feature.geometry as { coordinates: [number, number] })
                  .coordinates;
                gl.easeTo({ center: [lng, lat], zoom });
              });
          });

          gl.on('click', 'job-pins', (event) => {
            const jobId: unknown = event.features?.[0]?.properties?.['jobId'];
            if (typeof jobId === 'string') onSelectJob(jobId);
          });

          for (const layer of ['job-clusters', 'job-pins']) {
            gl.on('mouseenter', layer, () => {
              gl.getCanvas().style.cursor = 'pointer';
            });
            gl.on('mouseleave', layer, () => {
              gl.getCanvas().style.cursor = '';
            });
          }

          setReady(true);
        });

        // The panel is a flex child, so its final size can arrive after the
        // map is constructed. Resizing alone is not enough: the initial
        // `bounds` fit is computed against whatever size the container had at
        // construction, so a late layout leaves the map parked at world zoom —
        // which in a panel this size renders as an empty grey square. Re-fit
        // once the container first reports a real size.
        let fitted = false;
        const observer = new ResizeObserver(([entry]) => {
          gl.resize();
          const box = entry?.contentRect;
          if (fitted || !box || box.width < 1 || box.height < 1) return;
          fitted = true;
          gl.fitBounds(AUSTRALIA_BOUNDS, { padding: 24, animate: false });
        });
        observer.observe(container);
        resizeObserver = observer;
      } catch {
        if (!cancelled) onUnavailable();
      }
    })();

    return () => {
      cancelled = true;
      resizeObserver?.disconnect();
      map?.remove();
      mapRef.current = null;
    };
    // Deliberately mount-only: re-running this would rebuild the GL context on
    // every filter change. Points flow in through the effect below instead.
  }, []);

  // Push the current filter's points into the existing source.
  useEffect(() => {
    if (!ready) return;
    const source = mapRef.current?.getSource('jobs');
    if (!source) return;
    // setData returns a promise in MapLibre 6; nothing to await here.
    void (source as GeoJSONSource).setData({
      type: 'FeatureCollection',
      features: points.map((point) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [point.longitude, point.latitude] },
        properties: {
          jobId: point.jobId,
          tone: point.tone,
          suburb: point.suburb,
          state: point.state,
        },
      })),
    });
  }, [points, ready]);

  return <div className="jobs-map__gl" ref={containerRef} />;
}
